import apiService from './api';
import type {
  Ticket,
  TicketCategory,
  TicketMessage,
  TicketNotification,
  NotificationPreference,
  TicketStats,
  TicketLabel,
  PaginatedResponse,
  TicketPriority,
} from '../types/ticket';

function extractData<T>(response: any): T {
  if (response.success && response.data !== undefined) {
    return response.data as T;
  }
  return response as T;
}

export const ticketService = {
  // ========================
  // User endpoints
  // ========================

  async getTickets(params?: {
    status?: string;
    category?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<Ticket>> {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.category) query.set('category', params.category);
    if (params?.page) query.set('page', params.page.toString());
    if (params?.page_size) query.set('page_size', params.page_size.toString());
    const qs = query.toString();
    const res = await apiService.get<any>(`/tickets/${qs ? `?${qs}` : ''}`);
    return extractData<PaginatedResponse<Ticket>>(res);
  },

  async createTicket(data: {
    title: string;
    description: string;
    category?: number;
    priority?: TicketPriority;
  }): Promise<Ticket> {
    const res = await apiService.post<any>('/tickets/', data);
    return extractData<Ticket>(res);
  },

  async getTicket(id: string): Promise<Ticket> {
    const res = await apiService.get<any>(`/tickets/${id}/`);
    return extractData<Ticket>(res);
  },

  async sendMessage(id: string, data: {
    content: string;
    is_internal?: boolean;
  }, files?: File[]): Promise<TicketMessage> {
    if (files && files.length > 0) {
      const formData = new FormData();
      formData.append('content', data.content);
      if (data.is_internal) formData.append('is_internal', 'true');
      files.forEach(f => formData.append('files', f));
      const res = await apiService.post<any>(`/tickets/${id}/messages/`, formData);
      return extractData<TicketMessage>(res);
    }
    const res = await apiService.post<any>(`/tickets/${id}/messages/`, data);
    return extractData<TicketMessage>(res);
  },

  async closeTicket(id: string): Promise<Ticket> {
    const res = await apiService.post<any>(`/tickets/${id}/close/`, {});
    return extractData<Ticket>(res);
  },

  async reopenTicket(id: string): Promise<Ticket> {
    const res = await apiService.post<any>(`/tickets/${id}/reopen/`, {});
    return extractData<Ticket>(res);
  },

  async rateTicket(id: string, rating: number, comment?: string): Promise<Ticket> {
    const res = await apiService.post<any>(`/tickets/${id}/rate/`, { rating, comment });
    return extractData<Ticket>(res);
  },

  async getCategories(): Promise<TicketCategory[]> {
    const res = await apiService.get<any>('/tickets/categories/');
    return extractData<TicketCategory[]>(res);
  },

  // ========================
  // Notifications
  // ========================

  async getNotifications(params?: {
    is_read?: boolean;
    page?: number;
  }): Promise<PaginatedResponse<TicketNotification>> {
    const query = new URLSearchParams();
    if (params?.is_read !== undefined) query.set('is_read', params.is_read.toString());
    if (params?.page) query.set('page', params.page.toString());
    const qs = query.toString();
    const res = await apiService.get<any>(`/tickets/notifications/${qs ? `?${qs}` : ''}`);
    return extractData<PaginatedResponse<TicketNotification>>(res);
  },

  async markNotificationRead(id: number): Promise<void> {
    await apiService.post<any>(`/tickets/notifications/${id}/read/`, {});
  },

  async markAllNotificationsRead(): Promise<void> {
    await apiService.post<any>('/tickets/notifications/read-all/', {});
  },

  async getUnreadCount(): Promise<number> {
    const res = await apiService.get<any>('/tickets/notifications/unread-count/');
    return extractData<{ count: number }>(res).count;
  },

  async getNotificationPreferences(): Promise<NotificationPreference> {
    const res = await apiService.get<any>('/tickets/notification-preferences/');
    return extractData<NotificationPreference>(res);
  },

  async updateNotificationPreferences(data: Partial<NotificationPreference>): Promise<NotificationPreference> {
    const res = await apiService.put<any>('/tickets/notification-preferences/', data);
    return extractData<NotificationPreference>(res);
  },

  // ========================
  // Admin endpoints
  // ========================

  async adminGetTickets(params?: {
    status?: string;
    priority?: string;
    category?: string;
    assignee?: string;
    search?: string;
    sla_breached?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<Ticket>> {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== '') query.set(k, v.toString());
      });
    }
    const qs = query.toString();
    const res = await apiService.get<any>(`/tickets/admin/${qs ? `?${qs}` : ''}`);
    return extractData<PaginatedResponse<Ticket>>(res);
  },

  async adminGetTicket(id: string): Promise<Ticket> {
    const res = await apiService.get<any>(`/tickets/admin/${id}/`);
    return extractData<Ticket>(res);
  },

  async adminAssignTicket(id: string, assigneeId: number): Promise<Ticket> {
    const res = await apiService.post<any>(`/tickets/admin/${id}/assign/`, { assignee_id: assigneeId });
    return extractData<Ticket>(res);
  },

  async adminChangeStatus(id: string, ticketStatus: string, note?: string): Promise<Ticket> {
    const res = await apiService.post<any>(`/tickets/admin/${id}/status/`, { status: ticketStatus, note });
    return extractData<Ticket>(res);
  },

  async adminChangePriority(id: string, priority: string): Promise<Ticket> {
    const res = await apiService.post<any>(`/tickets/admin/${id}/priority/`, { priority });
    return extractData<Ticket>(res);
  },

  async adminUpdateLabels(id: string, labelIds: number[]): Promise<Ticket> {
    const res = await apiService.post<any>(`/tickets/admin/${id}/labels/`, { label_ids: labelIds });
    return extractData<Ticket>(res);
  },

  async adminReply(id: string, data: {
    content: string;
    is_internal?: boolean;
  }, files?: File[]): Promise<TicketMessage> {
    if (files && files.length > 0) {
      const formData = new FormData();
      formData.append('content', data.content);
      if (data.is_internal) formData.append('is_internal', 'true');
      files.forEach(f => formData.append('files', f));
      const res = await apiService.post<any>(`/tickets/admin/${id}/reply/`, formData);
      return extractData<TicketMessage>(res);
    }
    const res = await apiService.post<any>(`/tickets/admin/${id}/reply/`, data);
    return extractData<TicketMessage>(res);
  },

  async adminBulkAction(data: {
    ticket_ids: string[];
    action: 'close' | 'set_high' | 'set_medium' | 'assign';
    assignee_id?: number;
  }): Promise<{ count: number }> {
    const res = await apiService.post<any>('/tickets/admin/bulk/', data);
    return extractData<{ count: number }>(res);
  },

  async adminGetStats(): Promise<TicketStats> {
    const res = await apiService.get<any>('/tickets/admin/stats/');
    return extractData<TicketStats>(res);
  },

  async adminGetLabels(): Promise<TicketLabel[]> {
    const res = await apiService.get<any>('/tickets/admin/labels/');
    return extractData<TicketLabel[]>(res);
  },
};
